/*
    Multi-selector with all users in a company
*/

import { useQuery } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import { Control, Controller, FieldPathByValue, FieldValues, useController } from 'react-hook-form';
import { ActionMeta } from 'react-select';

import { GetAllCompanyUsersDocument } from '@willow/graphql-iso/src/app';
import { CompanyId, UserId } from '@willow/types-iso';

import { getUserName } from '../../../utils';
import { Loader } from '../../Loader/Loader';
import { WillowMultiSelect } from './WillowSelect';

type Props<T extends FieldValues> = {
  companyId: CompanyId;
  fieldName: FieldPathByValue<T, UserId[] | undefined>;
  control: Control<T>;
  hideErrorMessage?: boolean;
  placeholder?: string;
  isClearable?: boolean;
  onSelectUpdate?: (
    actionMeta: ActionMeta<{
      value: UserId;
      label: string;
    }>,
  ) => void;
};

export function ControlledCompanyUserMultiSelect<T extends FieldValues>({
  companyId,
  fieldName,
  control,
  hideErrorMessage = false,
  placeholder = 'Select',
  isClearable = false,
  onSelectUpdate,
}: Props<T>) {
  const {
    formState: { errors },
  } = useController({ control, name: fieldName });

  const { data, loading } = useQuery(GetAllCompanyUsersDocument, {
    variables: {
      companyId,
    },
  });
  if (loading || !data) return <Loader />;

  const userOptions = data.company.allUsers.map((u) => {
    return { value: u.id, label: getUserName(u) };
  });

  return (
    <>
      <Controller
        name={fieldName}
        control={control}
        render={({ field: { onChange, value } }) => (
          <WillowMultiSelect
            placeholder={placeholder}
            options={userOptions}
            value={value ? userOptions.filter((r) => value.includes(r.value)) : []}
            isClearable={isClearable}
            onChange={(users, actionMeta) => {
              if (onSelectUpdate) onSelectUpdate(actionMeta);
              onChange(users ? users.map((u) => u.value) : []);
            }}
          />
        )}
      />
      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
